import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Popover,
  ListBox,
  ListBoxItem,
  Input,
  type Selection,
} from 'react-aria-components';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cx } from './cx';
import { Icon } from './Icon';
import { Chip } from './Chip';
import { typography } from './Text';
import type { SelectOption } from './Select';
import { useT } from '../../modules/i18n';

export interface SearchableMultiSelectProps {
  options: SelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  /**
   * Suppresses the stacked visible label (e.g. to sit inline in a filter bar
   * alongside unlabeled pill controls) while still exposing `label` as the
   * trigger's `aria-label` — never drop accessibility, only the visible text.
   */
  hideLabel?: boolean;
  placeholder?: string;
  /** Placeholder for the in-dropdown search input. */
  searchPlaceholder?: string;
  hint?: React.ReactNode;
  /** Shown in the dropdown when the search filters everything out. */
  noOptionsText?: string;
  /**
   * Show an individual removable chip per value while at most this many are
   * selected; beyond it (or when 0, the default) collapse to one compact
   * "N selected" pill. Use a small value for form pickers where seeing the
   * picks matters; leave 0 for filters that should always stay compact.
   */
  maxChips?: number;
  isRequired?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  fullWidth?: boolean;
  className?: string;
}

const checkbox =
  'flex size-4 shrink-0 items-center justify-center rounded border text-base-white transition ' +
  'border-strong bg-surface group-selected:border-brand-solid group-selected:bg-brand-solid';

/**
 * Searchable multi-select — a bordered trigger that renders selected values as
 * removable Chips, opening a dropdown with a type-to-filter search box and a
 * checkbox list (multiple selection). Combines the search of
 * {@link SearchableSelect} with the multi-value model of {@link MultiSelect};
 * use it to pick several items from a long list (e.g. services to promote).
 * Built from the Chip primitive + react-aria + foundation tokens.
 */
export const SearchableMultiSelect = ({
  options,
  value,
  onChange,
  label,
  hideLabel,
  placeholder,
  searchPlaceholder,
  hint,
  noOptionsText,
  maxChips = 0,
  isRequired,
  isDisabled,
  isInvalid,
  errorMessage,
  fullWidth,
  className,
}: SearchableMultiSelectProps) => {
  const t = useT();
  const resolvedPlaceholder =
    placeholder ?? t('ui.searchableMultiSelect.placeholder');
  const resolvedSearchPlaceholder =
    searchPlaceholder ?? t('ui.searchableMultiSelect.searchPlaceholder');
  const resolvedNoOptionsText =
    noOptionsText ?? t('ui.searchableMultiSelect.noOptionsText');
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Focus the search box when the dropdown opens (autoFocus is an a11y lint).
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const selected = options.filter(o => value.includes(o.value));
  const invalid = isInvalid ?? Boolean(errorMessage);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, query]);

  // Set while a pointer press is in progress on the list, so a pick made with
  // the mouse can hand focus back to the search box (react-aria moves it onto
  // the pressed option). Keyboard picks leave focus on the list.
  const pointerPickRef = useRef(false);

  const handleSelectionChange = (keys: Selection) => {
    onChange(
      keys === 'all' ? options.map(o => o.value) : [...keys].map(String),
    );
    if (pointerPickRef.current) {
      // After react-aria has focused the option: put the caret back in the
      // search box with the query selected, so typing the next service
      // replaces it without a click on the input first.
      requestAnimationFrame(() => {
        searchRef.current?.focus();
        searchRef.current?.select();
      });
    }
  };

  const toggleDropdown = () => {
    if (!isDisabled) setOpen(o => !o);
  };

  return (
    <div
      className={cx('flex flex-col gap-1.5', fullWidth && 'w-full', className)}
    >
      {label && !hideLabel && (
        <span className={cx(typography.label, 'text-fg-secondary')}>
          {label}
          {isRequired && <span className="ml-0.5 text-brand-text">*</span>}
        </span>
      )}
      <div
        ref={triggerRef}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        aria-disabled={isDisabled || undefined}
        onClick={toggleDropdown}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleDropdown();
          }
        }}
        className={cx(
          'flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-lg border border-strong bg-surface px-2 py-1.5 shadow-xs outline-none transition',
          isDisabled
            ? 'cursor-not-allowed opacity-60'
            : 'cursor-pointer hover:border-brand-300 focus-visible:border-brand-300 focus-visible:ring-4 focus-visible:ring-[rgb(var(--c-ring)/0.24)]',
          open && 'border-brand-300 ring-4 ring-[rgb(var(--c-ring)/0.24)]',
          invalid && 'border-error-500',
        )}
      >
        {selected.length === 0 && (
          <span className={cx(typography.body, 'px-1 text-fg-muted')}>
            {resolvedPlaceholder}
          </span>
        )}
        {selected.length > 0 && selected.length <= maxChips
          ? // A removable chip per value while the selection is small enough.
            selected.map(o => (
              // Presentational wrapper — the interactive element is the Chip.
              <span
                key={o.value}
                role="presentation"
                onClick={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
              >
                <Chip
                  label={o.label}
                  removeLabel={t(
                    'ui.searchableMultiSelect.removeChipAriaLabel',
                    { label: o.label },
                  )}
                  onRemove={() => onChange(value.filter(v => v !== o.value))}
                />
              </span>
            ))
          : selected.length > 0 && (
              // Otherwise one compact pill so the control stays short. × clears
              // all; remove individual values by re-opening and unchecking.
              <span
                role="presentation"
                onClick={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
              >
                <Chip
                  label={
                    selected.length === 1
                      ? selected[0].label
                      : t('ui.searchableMultiSelect.selectedCount', {
                          count: selected.length,
                        })
                  }
                  removeLabel={t('ui.searchableMultiSelect.clearSelection')}
                  onRemove={() => onChange([])}
                />
              </span>
            )}
        <Icon
          as={ChevronDown}
          size="sm"
          className="ml-auto shrink-0 text-fg-muted"
        />
      </div>
      {hint && !errorMessage && (
        <span className={cx(typography.body, 'text-fg-tertiary')}>{hint}</span>
      )}
      {errorMessage && (
        <span className={cx(typography.body, 'text-error')}>
          {errorMessage}
        </span>
      )}
      <Popover
        triggerRef={triggerRef}
        isOpen={open}
        placement="bottom start"
        onOpenChange={next => {
          setOpen(next);
          if (!next) setQuery('');
        }}
        // The trigger is a plain element (not a RAC trigger), so without this
        // the click that opens the popover is itself seen as an outside
        // interaction and closes it again. Treat clicks within the trigger as
        // inside so it opens reliably.
        shouldCloseOnInteractOutside={el => !triggerRef.current?.contains(el)}
        className="min-w-64 w-(--trigger-width) rounded-lg border border-default bg-surface p-1 shadow-lg outline-none entering:animate-in entering:fade-in-0"
      >
        <div className="flex items-center gap-2 border-b border-default px-2.5 py-2">
          <Icon
            as={Search}
            size="sm"
            strokeWidth={1.75}
            className="shrink-0 text-fg-muted"
          />
          <Input
            ref={searchRef}
            aria-label={t('ui.searchableMultiSelect.searchAriaLabel')}
            placeholder={resolvedSearchPlaceholder}
            value={query}
            onChange={e => setQuery(e.target.value)}
            className={cx(
              typography.body,
              'min-w-0 flex-1 appearance-none border-0 bg-transparent text-fg outline-none placeholder:text-fg-muted',
            )}
          />
        </div>
        <ListBox
          // Capture phase: react-aria selects a mouse pick on the option's
          // own pointerdown, before a bubbling handler here would run.
          onPointerDownCapture={() => {
            pointerPickRef.current = true;
          }}
          onPointerUp={() => {
            // After the press: react-aria selects on pointer down or up
            // depending on the pointer type.
            setTimeout(() => {
              pointerPickRef.current = false;
            });
          }}
          selectionMode="multiple"
          selectedKeys={new Set(value)}
          onSelectionChange={handleSelectionChange}
          items={filtered}
          renderEmptyState={() => (
            <div
              className={cx(typography.body, 'px-2.5 py-2 text-fg-tertiary')}
            >
              {resolvedNoOptionsText}
            </div>
          )}
          className="mt-1 max-h-72 overflow-auto outline-none"
        >
          {(option: SelectOption) => (
            <ListBoxItem
              id={option.value}
              textValue={option.label}
              className="group flex cursor-pointer select-none items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-fg-secondary outline-none data-[focused]:bg-muted-strong data-[selected]:text-fg data-[selected]:font-medium"
            >
              <span className={checkbox}>
                <Icon
                  as={Check}
                  size="xs"
                  strokeWidth={3}
                  className="opacity-0 group-selected:opacity-100"
                />
              </span>
              <span className="flex min-w-0 items-center gap-2">
                {option.icon}
                <span className="truncate">{option.label}</span>
              </span>
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </div>
  );
};
